"use client";
import { tagular, cohesion } from "@redventures/cohesion-utils-react";
import { handleTagularDataTags, handleTagularIdentify } from "./eventing";
import logger from "@/lib/logger";
import { useEffect } from "react";
import { getAnonymousId, getSessionId } from "@/utils/tagular/helpers";
import { getUserInfoFromJWT } from "@/lib/auth/getUserInfo";
import { useDAPI } from "@/hooks/useDAPI";

const listenForCohesionErrors = () => {
  /**
   * Log an error if the event fails and ship error to New Relic
   */
  cohesion(
    "tagular:eventCompleted",
    (event: { properties?: { "@type"?: string } }, xhr) => {
      if (xhr.status !== 200) {
        const message = "Tagular event failed";
        const details = {
          eventName: event?.properties?.["@type"],
          message: xhr.responseText,
          status: xhr.status,
        };

        logger.error(message, details);
        window.newrelic.noticeError(new Error(message), details);
      }
    },
  );
};

const waitForNewRelic = (callback: () => void) => {
  const timeout = setTimeout(() => {
    if (window.newrelic) {
      callback();
    } else {
      clearTimeout(timeout);
      waitForNewRelic(callback);
    }
  }, 100);
};

// exponentially backoff and retry availability of Segment
const retryUntilSegmentReady = (
  callback: () => void,
  remainingAttempts = 10,
  delay = 50,
) => {
  if (window.analytics && typeof window.analytics.ready === "function") {
    window.analytics.ready(callback);
  } else {
    remainingAttempts--;
    if (remainingAttempts <= 0) {
      return;
    }
    setTimeout(function () {
      retryUntilSegmentReady(callback, remainingAttempts, delay * 2); // next exponential delay
    }, delay);
  }
};

const listenForConsentChanges = (fireDAPIEvent: () => void) => {
  /**
   * Listen for consent changes and fire DAPI event when C0002 consent is captured
   */
  cohesion("consent:changed", function (groups: string[]) {
    //Need to check for OneTrust and OnetrustActiveGroups to ensure that the consent:changed event
    //groups are accurate, since before this the listener runs with all groups by default
    if (!window.OneTrust || !window.OnetrustActiveGroups) {
      return;
    }
    const consentCaptured = Boolean(
      groups?.find((group: string) => group === "C0002"),
    );
    if (consentCaptured) {
      // Fire DAPI correlation event post-consent
      // TODO: IS IT THIS NEEDE ONLY FOR DEGREES?
      fireDAPIEvent();
    }
  });
};

function extractSegmentUserId() {
  const segmentUserId = window.analytics.user().id();
  if (segmentUserId) {
    return segmentUserId;
  }

  // Try to extract user ID from JWT cookie
  const jwt = getUserInfoFromJWT();
  const cookieUserId = jwt?.user_id ?? jwt?.sub;
  if (cookieUserId) {
    return String(cookieUserId);
  }

  return null;
}

// 1. Set the Segment AnonymousId to the one from Cohesion
// 2. Call Cohesion Identify with corresponding Segment identifiers
// 3. Call Segment Identify with the Cohesion Identifiers
// The AnonymousIds should not differ as they both use the same cookie to track that
//
const setCohesionSegmentIdentifiers = () => {
  const cohesionAnonymId = getAnonymousId() as string;
  const segmentPrevAnonymId = window.analytics.user().anonymousId();
  const segmentUserId = extractSegmentUserId();
  const segmentTraits = {
    cohesion_anonymous_id: cohesionAnonymId,
  };

  // Set Authenticated user Id in Cohesion if it exists, otherwise delete the authenticated user Id (logout)
  if (segmentUserId) {
    tagular("setAuthId", segmentUserId);
  } else {
    tagular("delAuthId");
  }

  window.analytics.setAnonymousId(cohesionAnonymId);

  const segmentAnonymId = window.analytics.user().anonymousId();

  handleTagularIdentify(
    cohesionAnonymId,
    segmentAnonymId,
    segmentPrevAnonymId,
    segmentUserId,
  );

  if (segmentUserId) {
    window.analytics.identify(segmentUserId, segmentTraits);
  } else {
    window.analytics.identify(segmentTraits);
  }
};

let hasSentNewRelicEvents = false;
export const CohesionReady = () => {
  const { fireDAPIEvent } = useDAPI();

  useEffect(() => {
    logger.info("Client Ready");
    const version = process.env.NEXT_PUBLIC_APP_VERSION || "";

    // Setting the identifiers as soon as both snippets are ready
    cohesion("tagular:ready", function () {
      retryUntilSegmentReady(setCohesionSegmentIdentifiers);
    });

    // Ensure Segment userId set from JWT cookie if available (independent of Cohesion)
    retryUntilSegmentReady(function () {
      const segmentUserId = extractSegmentUserId();
      if (segmentUserId) {
        window.analytics.identify(segmentUserId);
      }
    });

    waitForNewRelic(() => {
      window.newrelic.addRelease(version, version);
      window.newrelic.setCustomAttribute("version", version);
    });

    cohesion("ready", function () {
      handleTagularDataTags();
      listenForCohesionErrors();
      listenForConsentChanges(fireDAPIEvent);

      retryUntilSegmentReady(function () {
        const cohesionAnonymId = getAnonymousId() as string;
        const cohesionSessionId = getSessionId() as string;
        const segmentAnonymId = window.analytics.user().anonymousId();
        const segmentUserId = extractSegmentUserId();

        waitForNewRelic(() => {
          if (hasSentNewRelicEvents) return;
          hasSentNewRelicEvents = true;
          window.newrelic.setCustomAttribute(
            "cohesionAnonymId",
            cohesionAnonymId,
            true,
          );
          window.newrelic.setCustomAttribute(
            "cohesionSessionId",
            cohesionSessionId,
            true,
          );
          window.newrelic.setCustomAttribute(
            "segmentAnonymId",
            segmentAnonymId,
            true,
          );
          window.newrelic.setCustomAttribute(
            "segmentUserId",
            segmentUserId,
            true,
          );
          window.newrelic.addPageAction("cohesionReady");
        });
      });
    });
  }, [fireDAPIEvent]);

  return null;
};
